<template>
    <div class="rounded-2xl"
        :class="{'border border-app bg-card/50 p-4 sm:p-4': hasCard}"
    >
        <div class="flex justify-between items-center">
            <div class="flex items-start justify-between gap-3 flex-wrap">
                <span class="text-body/80 text-sm">{{ sessionDateTime(item) }}</span>
                <span v-if="location"
                    class="inline-flex items-center gap-1 text-brand-500 text-sm font-medium shrink-0">
                    <MapPin class="h-3.5 w-3.5" />
                    {{ location }}
                </span>
            </div>
            <div v-if="canAddToCalendar" class="flex justify-end">
                <button type="button" @click="addToCalendar(item, { location: location || undefined, eventTitle })"
                    class="inline-flex items-center gap-2 rounded-lg bg-card border border-app px-3.5 py-2 text-xs font-semibold text-header
                    transition-colors hover:border-brand-500 hover:text-brand-500">
                    <CalendarPlus class="h-4 w-4" />
                    Calendar
                </button>
            </div>
        </div>

        <h4 class="text-header font-bold text-lg leading-snug mt-2">{{ item.title }}</h4>

        <div class="flex flex-wrap items-center gap-2 mt-3">
            <span v-if="speakerName(item)" class="inline-flex items-center gap-2 text-body/80 text-sm">
                <User class="h-4 w-4" />
                {{ speakerName(item) }}
            </span>
            <span v-if="item.track" class="badge-outline">{{ item.track }}</span>
            <span v-if="item.isVirtual" class="badge-brand">Virtual</span>
            <span v-else-if="item.isPhysical" class="badge-outline">In person</span>
        </div>

        <p v-if="item.description" class="text-body text-sm mt-3 leading-relaxed">{{ item.description }}</p>

        
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { MapPin, User, CalendarPlus } from 'lucide-vue-next';
import { EventAgendaItem } from '~/types';
import { sessionDateTime, speakerName, addToCalendar, parseDate } from './agenda';

const props = defineProps<{
    item: EventAgendaItem;
    eventLocation?: string | null;
    eventTitle?: string;
    hasCard: boolean;
}>();

const location = computed(() => props.item.location || props.eventLocation || '');
const canAddToCalendar = computed(() => !!parseDate(props.item.startTime));
</script>

<style scoped>


.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

.text-body {
    color: rgb(var(--core-body-text));
}

.badge-outline {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    border-radius: 0.375rem;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgb(var(--core-muted-text));
    background-color: rgb(var(--core-muted-text) / 0.1);
    border: 1px solid rgb(var(--core-app-border));
}

.badge-brand {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    border-radius: 0.375rem;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgb(var(--core-brand-600));
    background-color: rgb(var(--core-brand-500) / 0.1);
}
</style>
